import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink } from "lucide-react";
import { useContent } from "@/lib/content";
import { getIcon } from "@/lib/icons";

export default function Courses({ onConsult }) {
  const { content } = useContent();
  const courses = content.courses || {};
  const slugs = Object.keys(courses);
  const [active, setActive] = useState(slugs[0] || "acca");
  const course = courses[active];
  if (!course || slugs.length === 0) return null;
  const Icon = getIcon(course.icon);

  return (
    <section id="courses" data-testid="courses-section" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <p className="font-inter text-sm font-semibold tracking-[0.18em] uppercase text-[#002570]">Career Programs</p>
            <h2 className="mt-3 font-rubik text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#0f172a] leading-tight text-balance">
              Choose the qualification that <span className="text-[#002570]">opens global doors</span>.
            </h2>
          </div>
          <p className="font-inter text-slate-600 max-w-md">
            Not sure which one fits you? Click any course to see the breakdown — or book a free consultation and let us help.
          </p>
        </div>

        <div className="flex flex-wrap gap-2 mb-8" role="tablist">
          {slugs.map((s) => (
            <button
              key={s}
              data-testid={`course-tab-${s.replace("-usa","")}`}
              onClick={() => setActive(s)}
              className={`px-5 py-2.5 rounded-full text-sm font-inter font-medium transition-all ${
                active === s ? "bg-[#002570] text-white shadow-md" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
            >
              {courses[s].name}
            </button>
          ))}
        </div>

        <div className="grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7 bg-[#f8fafc] rounded-3xl p-8 md:p-10 border border-slate-100" data-testid={`course-detail-${active.replace("-usa","")}`}>
            <div className="flex items-start gap-5 mb-6">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#002570] to-[#001a52] text-white flex items-center justify-center shadow-lg">
                <Icon className="w-7 h-7" />
              </div>
              <div>
                <h3 className="font-rubik text-2xl md:text-3xl font-bold text-[#0f172a]">{course.name}</h3>
                <p className="font-inter text-sm text-slate-600 mt-1">{course.full_name}</p>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-5 mb-6">
              <div className="bg-white rounded-2xl p-4 border border-slate-100">
                <p className="font-inter text-xs font-semibold uppercase tracking-wider text-slate-500">Duration</p>
                <p className="mt-1 font-rubik text-lg font-semibold text-[#0f172a]">{course.duration}</p>
              </div>
              <div className="bg-white rounded-2xl p-4 border border-slate-100">
                <p className="font-inter text-xs font-semibold uppercase tracking-wider text-slate-500">Levels</p>
                <p className="mt-1 font-rubik text-sm font-semibold text-[#0f172a]">{(course.levels || []).map((l) => l.name).join(" · ")}</p>
              </div>
            </div>

            <div className="mb-5">
              <p className="font-inter text-xs font-semibold uppercase tracking-wider text-slate-500">Tagline</p>
              <p className="mt-1 font-inter text-base text-slate-700">{course.tagline}</p>
            </div>

            <div className="mb-6">
              <p className="font-inter text-xs font-semibold uppercase tracking-wider text-slate-500">Career opportunities</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {(course.careers || []).slice(0, 4).map((c, i) => (
                  <span key={i} className="inline-block bg-[#73c3ff]/15 text-[#002570] text-xs font-inter font-medium px-3 py-1.5 rounded-full">
                    {c.title}
                  </span>
                ))}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-5 border-t border-slate-200">
              <div>
                <p className="font-inter text-xs font-semibold uppercase tracking-wider text-slate-500">Earning Potential</p>
                <p className="mt-1 font-rubik text-base md:text-lg font-bold text-[#002570]">{course.salary_summary}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Link
                  to={`/courses/${active}`}
                  data-testid={`course-view-page-${active.replace("-usa","")}`}
                  className="inline-flex items-center gap-2 bg-white border-2 border-[#002570] text-[#002570] hover:bg-[#002570] hover:text-white rounded-full px-5 py-3 text-sm font-inter font-medium transition-all"
                >
                  Full {course.name} Page <ExternalLink className="w-4 h-4" />
                </Link>
                <button
                  onClick={onConsult}
                  data-testid={`course-cta-${active.replace("-usa","")}`}
                  className="inline-flex items-center gap-2 bg-[#002570] text-white hover:bg-[#001a52] rounded-full px-5 py-3 text-sm font-inter font-medium transition-all hover:shadow-lg"
                >
                  Free Career Plan <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4">
            {slugs.filter((s) => s !== active).map((s) => {
              const c = courses[s];
              const I = getIcon(c.icon);
              return (
                <button
                  key={s}
                  onClick={() => setActive(s)}
                  data-testid={`course-mini-${s.replace("-usa","")}`}
                  className="text-left bg-white rounded-2xl p-5 border border-slate-100 hover:border-[#73c3ff] hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 group"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-11 h-11 rounded-xl bg-[#73c3ff]/15 text-[#002570] flex items-center justify-center group-hover:bg-[#002570] group-hover:text-white transition-colors">
                      <I className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <p className="font-rubik text-base font-bold text-[#0f172a]">{c.name}</p>
                      <p className="font-inter text-xs text-slate-500 line-clamp-1">{c.full_name}</p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-[#002570] group-hover:translate-x-1 transition-all" />
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
